import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockCloudDisks } from '../cloud-pcs/mockData';
import type { CloudDisk } from '../cloud-pcs/types';

export default function CloudDisksPage() {
  const [data, setData] = useState<CloudDisk[]>(mockCloudDisks);
  const [filteredData, setFilteredData] = useState<CloudDisk[]>(mockCloudDisks);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchType, setSearchType] = useState<'id' | 'userId' | 'phone'>('id');
  const [searchValue, setSearchValue] = useState('');
  const [createTimeStart, setCreateTimeStart] = useState('');
  const [createTimeEnd, setCreateTimeEnd] = useState('');
  const [expireTimeStart, setExpireTimeStart] = useState('');
  const [expireTimeEnd, setExpireTimeEnd] = useState('');
  const [isOverdue, setIsOverdue] = useState<'all' | 'yes' | 'no'>('all');

  const handleSearch = () => {
    let result = [...data];

    if (searchValue) {
      result = result.filter((item) => {
        if (searchType === 'id') {
          return item.id.toLowerCase().includes(searchValue.toLowerCase());
        } else if (searchType === 'userId') {
          return item.userId.toLowerCase().includes(searchValue.toLowerCase());
        } else {
          return item.userPhone.includes(searchValue);
        }
      });
    }

    if (isOverdue !== 'all') {
      result = result.filter((item) =>
        isOverdue === 'yes' ? item.isOverdue : !item.isOverdue
      );
    }

    setFilteredData(result);
  };

  const handleReset = () => {
    setSearchType('id');
    setSearchValue('');
    setCreateTimeStart('');
    setCreateTimeEnd('');
    setExpireTimeStart('');
    setExpireTimeEnd('');
    setIsOverdue('all');
    setFilteredData(data);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredData.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const handleToggleStatus = (id: string) => {
    const item = data.find((d) => d.id === id);
    if (item) {
      const newStatus = item.status === 'normal' ? 'disabled' : 'normal';
      const action = newStatus === 'disabled' ? '禁用' : '恢复';
      if (window.confirm(`确定要${action}该云硬盘吗？`)) {
        const newData = data.map((d) =>
          d.id === id ? { ...d, status: newStatus } : d
        );
        setData(newData);
        setFilteredData(
          filteredData.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
        );
        alert(`${action}成功`);
      }
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('确定要删除该云硬盘吗？\n此操作不可恢复！')) {
      setData(data.filter((item) => item.id !== id));
      setFilteredData(filteredData.filter((item) => item.id !== id));
      alert('删除成功');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('已复制到剪贴板');
  };

  const statistics = {
    totalCount: data.length,
    normalCount: data.filter((item) => item.status === 'normal').length,
    disabledCount: data.filter((item) => item.status === 'disabled').length,
    overdueCount: data.filter((item) => item.isOverdue).length,
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">云硬盘管理</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">云硬盘总数</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.totalCount}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <i className="ri-hard-drive-2-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">正常状态</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.normalCount}</p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">已禁用</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.disabledCount}</p>
              </div>
              <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                <i className="ri-forbid-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">已欠费</p>
                <p className="text-3xl font-bold text-gray-900">{statistics.overdueCount}</p>
              </div>
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-4">
          <div className="flex items-center gap-4 mb-4">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as 'id' | 'userId' | 'phone')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="id">云硬盘ID</option>
              <option value="userId">用户ID</option>
              <option value="phone">用户手机号</option>
            </select>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="请输入搜索内容"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-search-line mr-2"></i>
              搜索
            </button>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-2"></i>
              重置
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">创建时间</label>
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={createTimeStart}
                  onChange={(e) => setCreateTimeStart(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">至</span>
                <input
                  type="datetime-local"
                  value={createTimeEnd}
                  onChange={(e) => setCreateTimeEnd(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">到期时间</label>
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={expireTimeStart}
                  onChange={(e) => setExpireTimeStart(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">至</span>
                <input
                  type="datetime-local"
                  value={expireTimeEnd}
                  onChange={(e) => setExpireTimeEnd(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">是否欠费</label>
              <select
                value={isOverdue}
                onChange={(e) => setIsOverdue(e.target.value as 'all' | 'yes' | 'no')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">全部</option>
                <option value="yes">是</option>
                <option value="no">否</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredData.length && filteredData.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    云硬盘ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    云硬盘名称
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    所属用户
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    规格
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    内网IP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    创建时间
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    到期时间
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    释放时间
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    所属机房
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    所属服务器
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.map((item) => {
                  const usagePercent = (item.usedStorage / item.totalStorage) * 100;

                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900">{item.id}</span>
                          <button
                            onClick={() => copyToClipboard(item.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <i className="ri-file-copy-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">{item.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="text-gray-900">{item.userId}</div>
                          <div className="text-gray-500">{item.userPhone}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-[200px]">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-900">{item.storageType}</span>
                            <span className="text-gray-600">
                              {item.usedStorage}GB / {item.totalStorage}GB
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                usagePercent >= 90
                                  ? 'bg-red-500'
                                  : usagePercent >= 80
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${usagePercent}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">{item.internalIp}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 whitespace-nowrap">
                          {item.createTime}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-sm whitespace-nowrap ${
                            item.isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'
                          }`}
                        >
                          {item.expireTime}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 whitespace-nowrap">
                          {item.releaseTime || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 whitespace-nowrap">
                          {item.datacenter}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 whitespace-nowrap">
                          {item.server}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button className="text-blue-600 hover:text-blue-700 text-sm whitespace-nowrap">
                            详情
                          </button>
                          <button
                            onClick={() => handleToggleStatus(item.id)}
                            className={`text-sm whitespace-nowrap ${
                              item.status === 'normal'
                                ? 'text-orange-600 hover:text-orange-700'
                                : 'text-green-600 hover:text-green-700'
                            }`}
                          >
                            {item.status === 'normal' ? '禁用' : '恢复'}
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
